import { clsx } from '@alexis/helpers/clsx';
import {
  dateApiFormat,
  dateDisplayFormat,
  enMonthLabels,
  enShortDayLabels,
} from '@alexis/helpers/date';
import { translateNumber } from '@alexis/helpers/translation';
import React, { useCallback, useContext, useMemo } from 'react';
import { useSelector } from 'react-redux';

import Skeleton from '@components/Skeleton';

import { FARE_CALENDAR_CORE_CONFIG_KEY } from '@constants/flight';

import { GlobalContext } from '@context/GlobalContext';

import { rearrangeDayLabels } from '@helpers/date';

import { getLocaleState, getTranslationsState, getCurrencyState } from '@redux/selectors';

import { FlightSearchType } from '@wegoTypes/flights/flightSearchType';
import { FareCalendarType } from '@wegoTypes/flights/metasearch/calendar';

import styles from './FareCalendar.module.scss';

export interface FareCalendarProps {
  className?: string;
  firstDayOfWeek?: number;
  limitFromMilliseconds?: number;
  limitTillMilliseconds?: number;
  month: number;
  onDateSelected(value: Date, priceType?: FareCalendarType): void;
  publicHolidayMap?: PublicHolidayMap;
  selectedFromDateMilliseconds?: number;
  selectedToDateMilliseconds?: number;
  year: number;
  calendarFares: { [date: string]: { amount: number } } | undefined;
  isLoadingFares: boolean;
  expensiveThreshold: number | undefined;
  cheapThreshold: number | undefined;
  passengerCount: number;
  activeOutboundDateMilliseconds: number;
  searchType?: FlightSearchType;
  forcedDisabled?: boolean;
}

const FareCalendar: React.FC<FareCalendarProps> = ({
  className,
  firstDayOfWeek,
  limitFromMilliseconds,
  limitTillMilliseconds,
  month,
  onDateSelected,
  publicHolidayMap,
  selectedFromDateMilliseconds,
  selectedToDateMilliseconds,
  year,
  calendarFares,
  isLoadingFares,
  expensiveThreshold,
  cheapThreshold,
  passengerCount,
  activeOutboundDateMilliseconds,
  searchType,
  forcedDisabled,
}) => {
  const locale = useSelector(getLocaleState);
  const translations = useSelector(getTranslationsState);
  const currency = useSelector(getCurrencyState);

  const { userCentricCoreConfigs } = useContext(GlobalContext);

  const fareCalendarCoreConfig = userCentricCoreConfigs.find(
    (coreConfig) => coreConfig.key === FARE_CALENDAR_CORE_CONFIG_KEY,
  );

  const isShowFareCalendarVariantA = fareCalendarCoreConfig?.value === '1';

  const dayLabels = translations.mini_weekdays as Array<string>;
  const monthLabels = translations.long_months as Array<string>;

  const defaultDayLabels = useMemo(
    () => rearrangeDayLabels(dayLabels || enShortDayLabels, firstDayOfWeek),
    [dayLabels, firstDayOfWeek],
  );

  const priceDecimalPlaces = useMemo(() => {
    if (!!calendarFares) {
      const maxPrice = Math.max(
        ...Object.keys(calendarFares).map((date) => calendarFares[date].amount),
      );

      const maxTotalPrice = maxPrice * passengerCount;

      if (maxTotalPrice >= 1000000) {
        return 2;
      } else if (maxTotalPrice >= 10000) {
        return 1;
      }

      return 0;
    }
    return 0;
  }, [calendarFares, passengerCount]);

  const getPriceForDisplay = useCallback(
    (dateFareAmount: number) => {
      const totalDateFare = dateFareAmount * passengerCount;
      const divisor = priceDecimalPlaces === 2 ? 1000000 : priceDecimalPlaces === 1 ? 1000 : 1;
      const amount = totalDateFare / divisor;

      let noOfDecimals = currency.numberOfDecimals || 0;

      if (priceDecimalPlaces) {
        const numberOfDigits = amount.toString().split('.')[0]?.length || 0;

        noOfDecimals = numberOfDigits > 2 ? 0 : numberOfDigits > 1 ? 1 : 2;
      }

      const amountToDisplay = Number(amount).toLocaleString('en-US', {
        maximumFractionDigits: noOfDecimals,
      });

      let unit = '';

      if (locale === 'fa') {
        unit = priceDecimalPlaces === 2 ? 'م' : priceDecimalPlaces === 1 ? 'ه' : '';
      } else {
        unit = priceDecimalPlaces === 2 ? 'M' : priceDecimalPlaces === 1 ? 'k' : '';
      }

      let symbol = currency.symbol.includes('$') ? '$' : '';

      return `${symbol}${translateNumber(amountToDisplay, locale === 'fa')}${
        !!unit ? ' ' : ''
      }${unit}`;
    },
    [priceDecimalPlaces, currency.numberOfDecimals, currency.symbol, locale, passengerCount],
  );

  const days = useMemo<Array<JSX.Element>>(() => {
    const firstDayOfMonth = new Date(year, month - 1, 1);
    firstDayOfMonth.setHours(0, 0, 0, 0);
    const lastDayOfMonth = new Date(year, month, 0, 23, 59, 59);
    const todayDateMilliseconds = new Date().setHours(0, 0, 0, 0);

    const days: Array<JSX.Element> = [];

    for (let n = 1; n < 43; n++) {
      const dayIndexOffset = firstDayOfWeek === 7 ? 1 : firstDayOfWeek === 6 ? 2 : 0;
      const dayIndex = (n - dayIndexOffset) % 7;
      const day = firstDayOfMonth.getDay();
      const firstDayOfMonthMilliseconds = firstDayOfMonth.getTime();
      const lastDayOfMonthMilliseconds = lastDayOfMonth.getTime();

      if (day === dayIndex && firstDayOfMonthMilliseconds <= lastDayOfMonthMilliseconds) {
        const date = firstDayOfMonth.getDate();
        const isPublicHoliday =
          (!!publicHolidayMap && !!publicHolidayMap[dateApiFormat(firstDayOfMonth)]) || false;
        const isToday = firstDayOfMonthMilliseconds === todayDateMilliseconds;
        const isSelectedFromDate =
          selectedFromDateMilliseconds &&
          selectedFromDateMilliseconds === firstDayOfMonthMilliseconds;
        const isInSelectedDateRange =
          selectedFromDateMilliseconds &&
          selectedToDateMilliseconds &&
          firstDayOfMonthMilliseconds > selectedFromDateMilliseconds &&
          firstDayOfMonthMilliseconds < selectedToDateMilliseconds;
        const isSelectedToDate =
          selectedToDateMilliseconds && selectedToDateMilliseconds === firstDayOfMonthMilliseconds;
        const isDisabled =
          (limitFromMilliseconds === undefined &&
            firstDayOfMonthMilliseconds < todayDateMilliseconds) ||
          (limitFromMilliseconds !== undefined &&
            firstDayOfMonthMilliseconds < limitFromMilliseconds) ||
          (limitTillMilliseconds !== undefined &&
            firstDayOfMonthMilliseconds > limitTillMilliseconds) ||
          forcedDisabled;

        const dateFare = calendarFares?.[dateApiFormat(new Date(firstDayOfMonthMilliseconds))];

        let priceType: FareCalendarType | undefined;
        let isStartOrEndDate = !!isSelectedFromDate || !!isSelectedToDate;

        let price;

        if (!!cheapThreshold && !!expensiveThreshold) {
          if (!!dateFare?.amount) {
            price = getPriceForDisplay(dateFare.amount);

            priceType =
              dateFare.amount <= cheapThreshold
                ? 'CHEAP'
                : dateFare.amount >= expensiveThreshold
                ? 'EXPENSIVE'
                : 'NORMAL';
          } else if (isShowFareCalendarVariantA) {
            priceType = 'NORMAL';
          }
        }

        days.push(
          <div
            data-index={n}
            data-testid={dateApiFormat(new Date(firstDayOfMonthMilliseconds))}
            role='gridcell'
            data-iso={dateApiFormat(new Date(firstDayOfMonthMilliseconds))}
            aria-disabled={isDisabled || undefined}
            aria-hidden={isDisabled}
            aria-selected={!!isSelectedFromDate || !!isSelectedToDate}
            data-pw={`${
              !!isInSelectedDateRange || !!isSelectedFromDate || !!isSelectedToDate
                ? 'datePicker_dateInSelectedRange'
                : isDisabled
                ? 'datePicker_unavailableDate'
                : 'datePicker_availableDate'
            }`}
            key={n}
            className={clsx(
              !!isSelectedFromDate && styles.fromDate,
              !!isSelectedToDate && styles.toDate,
              !!selectedToDateMilliseconds && styles.hasToDate,
              !!isInSelectedDateRange && styles.inRange,
            )}
            onClick={
              // allow onClick for disabled dates in oneway and roundtrip and handle past dates click in upper functions. reason: let AI agents easily correct its misclicks.
              // multicity stay the same, no onClick if disabled.
              (searchType === 'multiCity' && isDisabled) || forcedDisabled
                ? undefined
                : () => onDateSelected(new Date(firstDayOfMonthMilliseconds), priceType)
            }
          >
            <div
              data-pw={`${isPublicHoliday ? 'datePicker_publicHoliday' : ''}`}
              className={clsx(
                styles.date,
                !!isSelectedFromDate && styles.start,
                !!isSelectedToDate && styles.end,
                isPublicHoliday && styles.publicHoliday,
                isToday && styles.today,
                !isDisabled && styles.selectable,
                !!isDisabled && styles.disabled,
                !isStartOrEndDate && priceType === 'CHEAP' && styles.cheapDate,
                !isStartOrEndDate && priceType === 'EXPENSIVE' && styles.expensiveDate,
                !isStartOrEndDate && priceType === 'NORMAL' && styles.normalDate,
                isShowFareCalendarVariantA && styles.variantA,
                isShowFareCalendarVariantA && isLoadingFares && styles.loading,
                firstDayOfMonthMilliseconds === activeOutboundDateMilliseconds && styles.active,
              )}
            >
              <span className={styles.dateNumber}>{translateNumber(date, locale === 'fa')}</span>

              {/* Fare for each date */}
              {!isShowFareCalendarVariantA && (
                <div className={styles.priceContainer}>
                  {isLoadingFares && !isDisabled && (
                    <Skeleton width={32} height={8} borderRadius={4} />
                  )}

                  {!isLoadingFares && !isDisabled && (
                    <span aria-label={`${translations.price}: ${price}`}>{price}</span>
                  )}
                </div>
              )}
            </div>
            <div
              role='button'
              data-ai-target='true'
              aria-label={dateDisplayFormat(
                new Date(firstDayOfMonthMilliseconds),
                translations.short_months as Array<string>,
                translations.short_weekdays as Array<string>,
                locale === 'fa',
                5,
              )}
              className={styles.srOnly}
            ></div>
          </div>,
        );
        firstDayOfMonth.setDate(date + 1);
      } else {
        if ((n === 29 || n === 36) && firstDayOfMonthMilliseconds >= lastDayOfMonthMilliseconds) {
          break;
        }
        days.push(<div data-index={n} key={n}></div>);
      }
    }
    return days;
  }, [
    activeOutboundDateMilliseconds,
    calendarFares,
    cheapThreshold,
    expensiveThreshold,
    firstDayOfWeek,
    forcedDisabled,
    getPriceForDisplay,
    isLoadingFares,
    isShowFareCalendarVariantA,
    limitFromMilliseconds,
    limitTillMilliseconds,
    locale,
    month,
    publicHolidayMap,
    searchType,
    selectedFromDateMilliseconds,
    selectedToDateMilliseconds,
    translations.price,
    translations.short_months,
    translations.short_weekdays,
    year,
  ]);

  return (
    <div data-testid='calendar-container' className={clsx(styles.container, className)}>
      <div className={styles.month}>
        {monthLabels ? monthLabels[month - 1] : enMonthLabels[month - 1]}{' '}
        {translateNumber(year, locale === 'fa')}
      </div>
      <div aria-hidden className={styles.dayLabels}>
        {defaultDayLabels.map((defaultDayLabel, index) => (
          <div key={index}>{defaultDayLabel}</div>
        ))}
      </div>

      <div role='grid' className={styles.days}>
        {days}
      </div>
    </div>
  );
};

export default FareCalendar;
